// src/hooks/useAffiliateQualification.js
//
// Dispara o sinal de INDICAÇÃO QUALIFICADA quando o usuário chega, de fato, na
// experiência principal do app.
//
// A CONDIÇÃO é a mesma que `PlataformaPessoal.jsx` usa para decidir entre
// mostrar o onboarding ou o AppShell:
//
//     isReady && objectives.length > 0   →   AppShell (o app de verdade)
//
// Espelhá-la aqui é o que faz "qualificado" significar exatamente "concluiu o
// onboarding e entrou no app", sem inventar um segundo conceito de onboarding
// nem tocar na tela que o redesign está reescrevendo.
//
// O sinal é só um aviso: o backend relê `PACIENTE/{uid}/OBJETIVO` antes de
// qualificar. Se este hook fosse a autoridade, bastaria um POST para fabricar
// indicações.

import { useEffect, useRef } from "react";
import { useUser } from "../contexts/UserContext";
import {
  alreadySignaled, markSignaled, signalQualification,
} from "../services/affiliateService";

export default function useAffiliateQualification() {
  // `loading` é o nome no contexto; os consumidores o apelidam de authLoading
  // (PlataformaPessoal faz igual). Destructurar `authLoading` direto daria
  // `undefined` — a guarda existiria no código e não na execução.
  const { currentUser, objectives, loading: authLoading } = useUser() || {};
  // Trava de execução: `objectives` chega por onSnapshot e muda várias vezes
  // no primeiro segundo. Sem isto, seriam várias chamadas por sessão.
  const enviando = useRef(false);

  const uid = currentUser?.uid;
  const concluiuOnboarding = Array.isArray(objectives) && objectives.length > 0;

  useEffect(() => {
    if (authLoading || !uid || !concluiuOnboarding) return;
    if (enviando.current || alreadySignaled(uid)) return;

    enviando.current = true;
    signalQualification()
      .then((r) => {
        // Só marca quando o backend deu um veredito definitivo. Um erro de
        // rede NÃO marca — o sinal precisa poder ser reenviado na próxima
        // abertura, senão uma indicação legítima se perde para sempre.
        if (["qualificada", "ja_qualificada", "sem_indicacao",
             "auto_indicacao"].includes(r?.status)) {
          markSignaled(uid);
        }
      })
      .catch(() => {/* silencioso: nada aqui pode atrapalhar o uso do app */})
      .finally(() => { enviando.current = false; });
  }, [authLoading, uid, concluiuOnboarding]);
}
